import { Controller } from "@hotwired/stimulus"

// Toggles between shareholder form and document upload for shareholders/partners question
export default class extends Controller {
  static targets = ["toggleCheckbox", "formContainer", "uploadContainer"]
  static outlets = ["tax-organizer--collection-form"]
  static values = {
    destroyDocumentsUrl: String,
    documentType: String
  }

  async toggle() {
    const isChecked = this.toggleCheckboxTarget.checked

    if (isChecked) {
      // User wants to switch to upload mode - check if there are existing shareholders
      const visibleRows = this.getVisibleShareholderRows()
      console.log('Switching to upload mode. Found', visibleRows.length, 'shareholders')

      if (visibleRows.length > 0) {
        // Confirm before deleting
        const confirmMsg = `You have ${visibleRows.length} shareholder(s) entered. Switching to document upload mode will delete all entered shareholder data. Continue?`
        if (!window.confirm(confirmMsg)) {
          // User cancelled - uncheck the box
          this.toggleCheckboxTarget.checked = false
          return
        }

        // User confirmed - delete all shareholders via server
        console.log('Deleting all shareholders...')
        await this.deleteAllShareholders()
      }

      // Show upload, hide form
      this.formContainerTarget.style.display = "none"
      this.uploadContainerTarget.style.display = "block"
    } else {
      // User wants to switch to form mode - check if there are uploaded documents
      const fileCount = this.getUploadedFilesCount()

      if (fileCount > 0) {
        // Confirm before deleting
        const confirmMsg = `You have ${fileCount} document(s) uploaded. Switching to manual entry mode will delete all uploaded documents. Continue?`
        if (!window.confirm(confirmMsg)) {
          // User cancelled - check the box again
          this.toggleCheckboxTarget.checked = true
          return
        }

        // User confirmed - delete all uploaded documents via server
        await this.deleteAllDocuments()
      }

      // Clear any file inputs in the upload container to prevent accidental re-submission
      this.clearFileInputs()

      // Show form, hide upload
      this.formContainerTarget.style.display = "block"
      this.uploadContainerTarget.style.display = "none"
    }
  }

  // Clear file inputs in the upload container to prevent ghost submissions
  clearFileInputs() {
    const fileInputs = this.uploadContainerTarget.querySelectorAll('input[type="file"]')
    fileInputs.forEach(input => {
      input.value = ''
    })
  }

  // Get visible shareholder rows (same logic as old business_dna_confirmation)
  getVisibleShareholderRows() {
    const rows = this.formContainerTarget.querySelectorAll('[data-tax-organizer--collection-form-target="row"]')
    return Array.from(rows).filter(row => {
      return row.style.display !== 'none' && !row.hidden
    })
  }

  // Get count of uploaded files from the upload container
  getUploadedFilesCount() {
    const filesContainer = this.uploadContainerTarget.querySelector('.existing-files')
    if (!filesContainer) return 0

    const fileIds = filesContainer.dataset.fileIds
    if (!fileIds) return 0

    return fileIds.split(',').filter(id => id.length > 0).length
  }

  async deleteAllShareholders() {
    if (!this.hasTaxOrganizerCollectionFormOutlet) {
      console.error('No collection-form outlet available')
      return
    }

    const destroyUrlTemplate = this.taxOrganizerCollectionFormOutlet.destroyUrlValue
    if (!destroyUrlTemplate) {
      console.error('No destroy URL available for shareholders')
      return
    }

    const destroyUrl = destroyUrlTemplate.replace(':id', 'all')
    console.log('Sending DELETE request to:', destroyUrl)

    try {
      const response = await fetch(destroyUrl, {
        method: 'DELETE',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      // Turbo Stream will handle the UI update (works for both 2xx and 422 responses)
      const html = await response.text()
      Turbo.renderStreamMessage(html)

      if (!response.ok) {
        console.error('Failed to delete shareholders:', response.statusText)
        // Uncheck the box on error
        this.toggleCheckboxTarget.checked = false
      }
    } catch (error) {
      console.error('Error deleting shareholders:', error)
      alert('An error occurred. Please try again.')
      // Uncheck the box on error
      this.toggleCheckboxTarget.checked = false
    }
  }

  async deleteAllDocuments() {
    if (!this.destroyDocumentsUrlValue) {
      console.error('No destroy documents URL available')
      return
    }

    if (!this.documentTypeValue) {
      console.error('No document type specified')
      return
    }

    try {
      // Delete all documents for this type via the 'all' endpoint
      const url = `${this.destroyDocumentsUrlValue}?document_type=${this.documentTypeValue}`
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      // Turbo Stream will handle the UI update (works for both 2xx and 422 responses)
      const html = await response.text()
      Turbo.renderStreamMessage(html)

      if (!response.ok) {
        console.error('Failed to delete documents:', response.statusText)
        // Re-check the box on error
        this.toggleCheckboxTarget.checked = true
      }
    } catch (error) {
      console.error('Error deleting documents:', error)
      alert('An error occurred. Please try again.')
      // Re-check the box on error
      this.toggleCheckboxTarget.checked = true
    }
  }
}
